import type { ProcedureType } from '@/types/cgt'
import type { Equipment, EquipmentPurpose, EquipmentTypeRow } from '@/types/equipment'

/**
 * Collapse a list of equipment units into one row per equipment type, counting
 * the units of each. Because schedules are defined per type, every unit of a
 * type shares the same schedule/calendar, so the first unit is representative.
 * Insertion order of first appearance is preserved.
 *
 * Only for mock mode, which has no type registry to read. Everywhere else the
 * planner reads types from `/equipment/types`: a type is created before any unit
 * of it exists, so a units-derived list cannot show one that was just added.
 */
export function deriveEquipmentTypes(rows: Equipment[]): EquipmentTypeRow[] {
  const byType = new Map<string, EquipmentTypeRow>()
  for (const eq of rows) {
    const existing = byType.get(eq.equipmentType)
    if (existing) {
      existing.units += 1
    } else {
      byType.set(eq.equipmentType, {
        id: eq.equipmentTypeId,
        equipmentType: eq.equipmentType,
        group: eq.group,
        // Purpose lives on the type, so every unit carries the same value and
        // the first one is representative — the same reason schedule and
        // calendar are taken from it.
        purpose: eq.purpose,
        // Inherited from the type, so the first unit is representative — the
        // same reason purpose, schedule and calendar are taken from it.
        maintained: eq.maintained,
        units: 1,
        schedule: eq.schedule,
        calendar: eq.calendar,
        preStartChecklist: eq.preStartChecklist,
      })
    }
  }
  return [...byType.values()]
}

/**
 * A unit standing in for its whole type, for the schedule and calendar editors.
 *
 * Both are type-level — they save by type name and read only `equipmentType`,
 * `schedule` and `calendar` — so a real unit is representative and, when the
 * type has none, a stand-in built from the type row is equally so. Without this
 * the Configure and Calendar buttons on a freshly created type do nothing at
 * all, which reads as a broken button rather than "add a unit first".
 *
 * The unit-shaped fields are placeholders and are never displayed by those two
 * editors; do not reuse this for anything that shows a unit.
 */
export function typeRepresentative(row: EquipmentTypeRow, units: Equipment[]): Equipment {
  const real = units.find((eq) => eq.equipmentType === row.equipmentType)
  if (real) return real
  return {
    id: row.id ?? row.equipmentType,
    equipmentId: row.equipmentType,
    equipmentType: row.equipmentType,
    equipmentTypeId: row.id ?? '',
    group: row.group,
    purpose: row.purpose,
    maintained: row.maintained,
    location: { site: '—', display: '—' },
    description: '',
    model: '—',
    serial: '—',
    vendor: '—',
    installed: 0,
    status: 'OPERATIONAL',
    schedule: row.schedule,
    calendar: row.calendar,
    preStartChecklist: row.preStartChecklist,
  }
}

/**
 * Narrow equipment-type rows by the planner toolbar's three axes.
 *
 * `group`/`purpose` are `null` for "all" rather than the toolbar's `'all'`
 * sentinel, so this module stays free of component imports (its tests run in
 * vitest's node environment, which cannot load `.tsx`).
 *
 * Matching is on the type's own attributes. The units tab searches unit labels
 * and locations, but a type has neither — searching "Suite-A" here would answer
 * with types rather than the units actually in that suite.
 */
export function filterEquipmentTypes(
  rows: EquipmentTypeRow[],
  filters: {
    search?: string
    group?: string | null
    purpose?: string | null
    /** `null` is "all"; true/false narrow to types that are / are not serviced. */
    maintained?: boolean | null
  },
): EquipmentTypeRow[] {
  const q = (filters.search ?? '').trim().toLowerCase()
  return rows.filter((row) => {
    // Group, purpose and maintained narrow together, deliberately: they are
    // independent axes, so "Cryopreservation + Storage + not maintained" has to
    // be askable. `maintained` is checked against null rather than falsiness —
    // `false` is a real choice here, not an absent one.
    if (filters.group && row.group !== filters.group) return false
    if (filters.purpose && row.purpose !== filters.purpose) return false
    if (filters.maintained != null && row.maintained !== filters.maintained) return false
    if (!q) return true
    return row.equipmentType.toLowerCase().includes(q) || row.group.toLowerCase().includes(q)
  })
}

/**
 * The kind of procedure a type's cycles run (z8nrz7ewt5): "Connecting Rooms to
 * Procedure = Cleaning, Connecting Equipment to Procedure = Equipment". The
 * server enforces the same rule.
 */
export function procedureTypeForPurpose(purpose: EquipmentPurpose): Extract<ProcedureType, 'Cleaning' | 'Equipment'> {
  return purpose === 'ROOM' ? 'Cleaning' : 'Equipment'
}
